import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, PreloadedQuery, useLazyLoadQuery, usePreloadedQuery, useQueryLoader, useRefetchableFragment } from "react-relay";
import { Link, Navigate, useOutletContext, useParams } from "react-router";
import { CHAT_VIEWABLE_SOURCES } from "src/common/chat-viewable-sources";
import { getRelayErrorMessage } from "src/common/utils";
import { ChatComposer } from "src/components/ui/chat/ChatComposer";
import { ThinkingContainer } from "src/components/ui/chat/ThinkingContainer";
import type { ChatAttachmentType } from "src/components/ui/chat/useChatAttachments";
import { ContentSuspense } from "src/components/ui/content-suspense";
import GraphQLErrorBoundary from "src/components/ui/graphql-error-boundary";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { getLastAgentResponse, useChatNotification } from "src/hooks/use-chat-notification";
import { usePusherEvent } from "src/hooks/use-pusher-event";
import { useToast } from "src/hooks/use-toast";
import type { LlmConversationChatViewableQuery as LlmConversationChatViewableQueryType } from "src/pages/AskPage/routes/__generated__/LlmConversationChatViewableQuery.graphql";

import LlmConversationMessageList from "../components/LlmConversationMessageList";
import { useInterruptAgentMutation } from "../hooks/useInterruptAgentMutation";
import { useSendLlmMessage } from "../hooks/useSendLlmMessage";
import { type AskPageOutletContext } from "../types";
import type { LlmConversationChatContent_llmConversation$key } from "./__generated__/LlmConversationChatContent_llmConversation.graphql";
import type { LlmConversationChatQuery as LlmConversationChatQueryType } from "./__generated__/LlmConversationChatQuery.graphql";
import type { LlmConversationChatV1RedirectQuery } from "./__generated__/LlmConversationChatV1RedirectQuery.graphql";

// Lightweight gate query — only fetches id + source to check viewability.
// Messages and interaction data are NOT included to avoid exposing
// internal prompting for non-viewable conversations.
const llmConversationChatQuery = graphql`
  query LlmConversationChatQuery($id: GlobalID!) {
    me {
      ...appMe
    }
    node(id: $id) {
      ... on GraphQLLlmConversation {
        id
        source
      }
    }
  }
`;

// Full content query — only loaded after the gate check confirms the conversation is viewable.
const llmConversationChatViewableQuery = graphql`
  query LlmConversationChatViewableQuery($id: GlobalID!) {
    node(id: $id) {
      ... on GraphQLLlmConversation {
        id
        source
        sourceId
        metadataJson
        createdAt
        updatedAt
        conversationId
        ...LlmConversationChatContent_llmConversation
      }
    }
  }
`;

const llmConversationChatV1RedirectQuery = graphql`
  query LlmConversationChatV1RedirectQuery($conversationId: Int!) {
    llmConversationByConversationId(conversationId: $conversationId) {
      ... on GraphQLLlmConversation {
        id
      }
    }
  }
`;

const llmConversationFragment = graphql`
  fragment LlmConversationChatContent_llmConversation on GraphQLLlmConversation
  @refetchable(queryName: "LlmConversationChatContentRefetchQuery") {
    id
    source
    sourceId
    metadataJson
    conversationId
    createdAt
    updatedAt
    hasUnreadResponse
    llmInteractions(last: 10) {
      id
      status
      statusMessage
    }
    messages {
      id
      messageRole
      messageType
      content
      messageMetadata {
        usage {
          billingTokens
        }
      }
    }
    ...LlmConversationMessageList_llmConversation
  }
`;

// Gate component — checks viewability from the lightweight query before loading content.
const LlmConversationChatContent: React.FC<{
  queryRef: PreloadedQuery<LlmConversationChatQueryType>;
  onRefetchReady?: (refetchFn: () => void) => void;
}> = ({ queryRef, onRefetchReady }) => {
  const data = usePreloadedQuery(llmConversationChatQuery, queryRef);
  const node = data?.node;

  if (!node?.id) {
    return null;
  }

  // Treat missing source as viewable (backward compat for legacy conversations without a source).
  const isViewable = Boolean(!node.source || CHAT_VIEWABLE_SOURCES.has(node.source));

  if (!isViewable) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p className="text-lg mb-2">Conversation not available</p>
          <p className="text-sm">This conversation is not viewable from the chat page.</p>
          <Link to="/chat" className="text-primary hover:text-primary/80 mt-4 inline-block">
            Back to Chat
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ContentSuspense height="h-full" fallback={null}>
      <LlmConversationChatViewableContent
        conversationId={node.id}
        onRefetchReady={onRefetchReady}
      />
    </ContentSuspense>
  );
};

// Full content component — only rendered after viewability is confirmed.
// Fetches messages, interactions, and all chat data.
const LlmConversationChatViewableContent: React.FC<{
  conversationId: string;
  onRefetchReady?: (refetchFn: () => void) => void;
}> = ({ conversationId, onRefetchReady }) => {
  const { messagesEndRef, userData, refetchSidebar, markConversationAsRead, isOnboarding } = useOutletContext<AskPageOutletContext>();

  const llmConversationData = useLazyLoadQuery<LlmConversationChatViewableQueryType>(
    llmConversationChatViewableQuery,
    { id: conversationId },
    { fetchPolicy: "store-and-network" },
  );

  // Use refetchable fragment for updates without Suspense
  const [llmConversation, refetch] = useRefetchableFragment(
    llmConversationFragment,
    llmConversationData?.node as LlmConversationChatContent_llmConversation$key
  );

  // Create refetch function that doesn't trigger Suspense
  const refetchLlmConversation = useCallback(() => {
    startTransition(() => {
      refetch({}, { fetchPolicy: "network-only" });
    });
  }, [refetch]);

  // Register the refetch function with parent on mount
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetchLlmConversation);
    }
  }, [onRefetchReady, refetchLlmConversation]);

  // All hooks must be declared before any conditional returns
  const { toast } = useToast();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const prevIsAtBottomRef = useRef(true);

  // Track if the bottom anchor (messagesEndRef) is visible
  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAtBottom(entry.isIntersecting);
        isAtBottomRef.current = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [messagesEndRef]);

  // Trigger 1: mark as read when navigating to a conversation that has an unread response.
  // No scroll check needed here — navigating to a conversation always starts at the bottom.
  const llmConversationId = llmConversation?.id;
  const hasUnreadResponse = llmConversation?.hasUnreadResponse;
  useEffect(() => {
    if (!llmConversationId || !hasUnreadResponse || !markConversationAsRead) return;
    markConversationAsRead(llmConversationId, refetchSidebar);
  }, [llmConversationId, hasUnreadResponse, markConversationAsRead, refetchSidebar]);

  // Trigger 3: mark as read when user scrolls to bottom
  useEffect(() => {
    const wasAtBottom = prevIsAtBottomRef.current;
    prevIsAtBottomRef.current = isAtBottom;
    if (!llmConversationId || !isAtBottom || wasAtBottom || !hasUnreadResponse || !markConversationAsRead) return;
    markConversationAsRead(llmConversationId, refetchSidebar);
  }, [llmConversationId, isAtBottom, hasUnreadResponse, markConversationAsRead, refetchSidebar]);

  // These hooks must also be before the early return
  const { sendMessage, isSending } = useSendLlmMessage({ llmConversationId: llmConversation?.id ?? "" });
  const { handleInterruptAgent } = useInterruptAgentMutation();
  const [isWaitingForInteraction, setIsWaitingForInteraction] = useState(false);
  const [isInterrupting, setIsInterrupting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [waitingToSendAfterInterrupt, setWaitingToSendAfterInterrupt] = useState(false);
  const chatInputRef = useRef<HTMLTextAreaElement & { setInputMessage?: (msg: string) => void }>(null);

  const isReadOnly = useMemo(() => {
    if (!llmConversation || !llmConversation.source) {
      return true;
    }
    return llmConversation.source !== "UI";
  }, [llmConversation]);

  const { activeInteractionId, activeStatusMessage } = useMemo(() => {
    const activeInteraction = llmConversation?.llmInteractions?.find(
      (interaction) => interaction.status === "PENDING" || interaction.status === "IN_PROGRESS"
    );
    return {
      activeInteractionId: activeInteraction?.id,
      activeStatusMessage: activeInteraction?.statusMessage ?? null,
    };
  }, [llmConversation]);

  const hasActiveInteraction = Boolean(activeInteractionId);

  useEffect(() => {
    if (hasActiveInteraction && isWaitingForInteraction) {
      setIsWaitingForInteraction(false);
    }
  }, [hasActiveInteraction, isWaitingForInteraction]);

  useEffect(() => {
    if (!isWaitingForInteraction || hasActiveInteraction) return;
    const timeoutId = setTimeout(() => {
      setIsWaitingForInteraction(false);
      toast({
        title: "Error",
        description: "Message could not be processed. Please try again.",
        variant: "error",
      });
    }, 30_000);
    return () => clearTimeout(timeoutId);
  }, [isWaitingForInteraction, hasActiveInteraction, toast]);

  const isLastMessageComplete = useMemo(() => {
    const messages = llmConversation?.messages;
    if (!messages || messages.length === 0) return false;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return false;

    const isAgentMessage = lastMessage.messageRole === "AGENT";
    if (!isAgentMessage) return false;

    if (lastMessage.messageType === "AGENT_PROCESS") return false;

    const hasMetadata = Boolean(lastMessage.messageMetadata);
    const hasContent = Boolean(lastMessage.content && lastMessage.content.length > 0);

    return hasMetadata && hasContent;
  }, [llmConversation]);

  const lastAgentResponse = useMemo(
    () => getLastAgentResponse(llmConversation?.messages),
    [llmConversation?.messages],
  );

  const combinedSendingState = isSending || isWaitingForInteraction || (hasActiveInteraction && !isLastMessageComplete);

  const { requestNotificationPermission } = useChatNotification({
    isWaitingForResponse: combinedSendingState,
    lastResponseText: lastAgentResponse.text,
    lastResponseId: lastAgentResponse.id,
  });

  const handleSend = useCallback(
    async (messageText: string, attachments: ChatAttachmentType[] = []) => {
      if (!llmConversation) return;
      const trimmedMessage = messageText.trim();
      if (!trimmedMessage) return;

      requestNotificationPermission();
      setIsWaitingForInteraction(true);

      const attachmentHandles = attachments
        .map((a) => a.handle)
        .filter((h): h is string => Boolean(h));

      try {
        await sendMessage(trimmedMessage, attachmentHandles.length ? { attachmentHandles } : undefined);
        refetchLlmConversation();
        refetchSidebar();
      } catch (error) {
        setIsWaitingForInteraction(false);
        const errorMessage = error instanceof Error ? getRelayErrorMessage(error) : "Failed to send message";
        toast({
          description: errorMessage,
          variant: "error",
        });
      }
    },
    [llmConversation, requestNotificationPermission, refetchLlmConversation, refetchSidebar, sendMessage, toast]
  );

  useEffect(() => {
    if (!hasActiveInteraction && isInterrupting) {
      setIsInterrupting(false);
    }
  }, [hasActiveInteraction, isInterrupting]);

  const handleInterrupt = useCallback(() => {
    if (!llmConversation) return;
    if (activeInteractionId) {
      setIsInterrupting(true);
      handleInterruptAgent(llmConversation.id, undefined, llmConversation.id, activeInteractionId);
    }
  }, [llmConversation, activeInteractionId, handleInterruptAgent]);

  const handleSubmitWhileDisabled = useCallback((message: string) => {
    setPendingMessage(message);
  }, []);

  const handleStopAndSend = useCallback(() => {
    if (!llmConversation) return;
    if (!llmConversation) return;
    if (pendingMessage && activeInteractionId) {
      chatInputRef.current?.setInputMessage?.("");
      setIsInterrupting(true);
      setWaitingToSendAfterInterrupt(true);
      handleInterruptAgent(llmConversation.id, undefined, llmConversation.id, activeInteractionId);
    }
  }, [pendingMessage, activeInteractionId, handleInterruptAgent, llmConversation]);

  useEffect(() => {
    if (waitingToSendAfterInterrupt && !hasActiveInteraction && pendingMessage) {
      handleSend(pendingMessage);
      setPendingMessage(null);
      setWaitingToSendAfterInterrupt(false);
    }
  }, [waitingToSendAfterInterrupt, hasActiveInteraction, pendingMessage, handleSend]);

  const handleDismissWarning = useCallback(() => {
    setPendingMessage(null);
    setWaitingToSendAfterInterrupt(false);
  }, []);

  useEffect(() => {
    if (!hasActiveInteraction && pendingMessage && !waitingToSendAfterInterrupt) {
      setPendingMessage(null);
    }
  }, [hasActiveInteraction, pendingMessage, waitingToSendAfterInterrupt]);

  // Listen for Pusher updates
  usePusherEvent<{ llm_conversation_id: string }>({
    channelId: llmConversation?.id ?? "",
    eventType: "LLM_CONVERSATION_UPDATED",
    onEvent: useCallback(() => {
      refetchLlmConversation();
      refetchSidebar();
      // Mark as read if user is viewing at the bottom — safe because
      // markConversationRead no longer emits a Pusher event (no feedback loop)
      if (isAtBottomRef.current && llmConversation?.id && markConversationAsRead) {
        markConversationAsRead(llmConversation.id, refetchSidebar);
      }
    }, [refetchLlmConversation, refetchSidebar, llmConversation?.id, markConversationAsRead]),
  });

  // Polling fallback: if a Pusher event is lost, periodically refetch to detect agent completion.
  useEffect(() => {
    if (!hasActiveInteraction) return;

    const interval = setInterval(() => {
      refetchLlmConversation();
      refetchSidebar();
    }, 20_000);

    return () => clearInterval(interval);
  }, [hasActiveInteraction, refetchLlmConversation, refetchSidebar]);

  if (!llmConversation) {
    return null;
  }

  const userInitials = `${userData?.firstName?.[0] ?? ""}${userData?.lastName?.[0] ?? ""}`;

  return (
    <div className="flex-1 flex flex-col bg-background relative z-10 min-h-full">
      {/* Messages container with gradient overlay */}
      <div className="flex-1 overflow-hidden relative">
        <LlmConversationMessageList
          llmConversation={llmConversation}
          userAvatar={userData?.pictureUrl}
          userInitials={userInitials}
          messagesEndRef={messagesEndRef}
          isPending={combinedSendingState}
          activeInteractionId={activeInteractionId}
          activeStatusMessage={activeStatusMessage}
          isOnboarding={isOnboarding}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent z-10"
        />
      </div>

      {/* Chat input - Floating sticky at bottom */}
      {!isReadOnly && (
        <div className="sticky bottom-0 pb-8 pt-3 px-8 flex justify-center z-20 pointer-events-none">
          <div className="w-full max-w-6xl pointer-events-auto">
            <ThinkingContainer
              isThinking={combinedSendingState}
              pendingMessage={pendingMessage}
              onStopAndSend={handleStopAndSend}
              onDismissWarning={handleDismissWarning}
              isStopAndSendPending={waitingToSendAfterInterrupt}
            >
              <form className="relative rounded-md bg-popover border border-border p-1.5 shadow-sm focus-within:border-primary/50 transition-colors duration-150">
                <ChatComposer
                  ref={chatInputRef}
                  placeholder="Ask anything about your systems"
                  sendDisabled={combinedSendingState}
                  onSend={handleSend}
                  onSubmitWhileDisabled={handleSubmitWhileDisabled}
                  showInterrupt={combinedSendingState}
                  onInterrupt={handleInterrupt}
                  isInterrupting={isInterrupting}
                  inputClassName="min-h-[36px] w-full resize-none border-0 bg-transparent py-1.5 text-sm text-foreground shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </form>
            </ThinkingContainer>
          </div>
        </div>
      )}

      {/* Read-only mode indicator */}
      {isReadOnly && (
        <div className="p-4 relative flex-shrink-0 border-t border-border">
          <div className="flex items-center justify-center text-muted-foreground text-sm">
            <span>This conversation is in read-only mode</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Component to handle V1 -> V2 redirect
const V1ConversationRedirect: React.FC<{ conversationId: number }> = ({ conversationId }) => {
  const data = useLazyLoadQuery<LlmConversationChatV1RedirectQuery>(
    llmConversationChatV1RedirectQuery,
    { conversationId },
    { fetchPolicy: "network-only" }
  );

  const llmConversation = data.llmConversationByConversationId;

  // Check if we got a GraphQLLlmConversation by checking if id exists (inline fragment)
  if (llmConversation?.id) {
    return <Navigate to={`/chat/c/${llmConversation.id}`} replace />;
  }

  // V1 conversation exists but has no associated V2 conversation
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center text-slate-400">
        <p className="text-lg mb-2">Conversation not found</p>
        <p className="text-sm">This conversation is not available in the new chat format.</p>
        <Link to="/chat" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Back to Chat
        </Link>
      </div>
    </div>
  );
};

// Wrapper component that checks if this is a V1 conversation first
const LlmConversationChat: React.FC = () => {
  const params = useParams<{ conversationId: string }>();
  const conversationId = params.conversationId!;

  // Decode the GlobalID to check if it's a V1 Conversation
  const decodedId = React.useMemo(() => {
    try {
      const decoded = atob(conversationId);
      const [typeName, numericId] = decoded.split(":");
      return { typeName, numericId: parseInt(numericId, 10) };
    } catch {
      return { typeName: null, numericId: null };
    }
  }, [conversationId]);

  // If it's a V1 Conversation ID, redirect to V2 using a Suspense boundary
  if (decodedId.typeName === "GraphQLConversation" && decodedId.numericId) {
    return (
      <ContentSuspense>
        <V1ConversationRedirect conversationId={decodedId.numericId} />
      </ContentSuspense>
    );
  }

  // Otherwise, render the V2 conversation
  return <LlmConversationChatV2 />;
};

// Main component that handles V2 conversation loading
const LlmConversationChatV2: React.FC = () => {
  const params = useParams<{ conversationId: string }>();
  const conversationId = params.conversationId!;
  const { refreshSignal, setConversationRefetch } = useOutletContext<AskPageOutletContext>();

  const prevConversationIdRef = useRef<string | null>(null);
  const errorKeyRef = useRef(0);
  const contentRefetchRef = useRef<(() => void) | null>(null);

  const [queryRef, loadQuery] = useQueryLoader<LlmConversationChatQueryType>(llmConversationChatQuery);

  useEffect(() => {
    const fetchPolicy = refreshSignal > 0 ? "store-or-network" : "network-only";
    loadQuery({ id: conversationId }, { fetchPolicy });
  }, [conversationId, refreshSignal, loadQuery]);

  const handleRefetchReady = (refetchFn: () => void) => {
    contentRefetchRef.current = refetchFn;
  };

  useEffect(() => {
    const refetchFn = () => {
      if (contentRefetchRef.current) {
        contentRefetchRef.current();
      } else {
        loadQuery({ id: conversationId }, { fetchPolicy: "store-and-network" });
      }
    };
    setConversationRefetch(conversationId, refetchFn);

    return () => {
      setConversationRefetch(conversationId, () => {});
      contentRefetchRef.current = null;
    };
  }, [conversationId, loadQuery, setConversationRefetch]);

  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      prevConversationIdRef.current = conversationId;
      errorKeyRef.current += 1;
    }
  }, [conversationId]);

  if (!queryRef) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner className="h-12 w-12 text-slate-400" />
      </div>
    );
  }

  return (
    <GraphQLErrorBoundary key={`error-boundary-${errorKeyRef.current}`}>
      <ContentSuspense height="h-full" fallback={null}>
        <LlmConversationChatContent
          queryRef={queryRef}
          onRefetchReady={handleRefetchReady}
        />
      </ContentSuspense>
    </GraphQLErrorBoundary>
  );
};

export default LlmConversationChat;
